import type {
  DeviceHealthProblem, DeviceHealthScope, DeviceHealthSummary, DeviceHealthTimelineEvent, DeviceHealthWindow,
} from '@platform/shared-types';

/**
 * device-health-service read routes (spec §6.1). Same host pattern as
 * fleetSupportApi: platform.api.<stage>.<apex>/device-health, and the
 * `/device-health-api` Vite proxy on localhost. Admin-pool token, raw.
 */
function detectStage(): 'prod' | 'staging' | 'dev' {
  const env = (import.meta.env.VITE_STAGE as string | undefined)?.toLowerCase();
  if (env === 'prod' || env === 'production') return 'prod';
  if (env === 'staging' || env === 'stage') return 'staging';
  if (env === 'dev') return 'dev';
  const h = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
  if (h.startsWith('admin.dev.') || h === 'localhost' || h.startsWith('127.')) return 'dev';
  if (h.includes('stage')) return 'staging';
  return 'prod';
}

export function resolveDeviceHealthHost(hostname: string, stage: 'prod' | 'staging' | 'dev', envBase?: string): string {
  if (envBase) return envBase;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return '/device-health-api';
  const apex = hostname.replace(/^(dev|stage|www|admin\.dev|admin\.stage|admin)\./, '');
  if (stage === 'prod') return `https://platform.api.${apex}/device-health`;
  if (stage === 'staging') return `https://platform.api.stage.${apex}/device-health`;
  return `https://platform.api.dev.${apex}/device-health`;
}

const host = () => resolveDeviceHealthHost(
  typeof window !== 'undefined' ? window.location.hostname : 'localhost',
  detectStage(),
  import.meta.env.VITE_DEVICE_HEALTH_API_BASE as string | undefined,
);

export class DeviceHealthApiError extends Error {
  status: number;
  constructor(status: number, body: string) {
    let msg = `device-health ${status}`;
    try { const j = JSON.parse(body); msg = j.message || j.error || msg; } catch { if (body) msg = body.slice(0, 200); }
    super(msg);
    this.status = status;
  }
}

async function get<T>(path: string): Promise<T> {
  const token = (typeof localStorage !== 'undefined' && localStorage.getItem('jwtToken')) || '';
  const res = await fetch(`${host()}${path}`, { headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: token } : {}) } });
  if (!res.ok) throw new DeviceHealthApiError(res.status, await res.text().catch(() => ''));
  return res.json() as Promise<T>;
}

const qs = (o: Record<string, string | undefined>) =>
  Object.entries(o).filter(([, v]) => v !== undefined && v !== '').map(([k, v]) => `${k}=${encodeURIComponent(v!)}`).join('&');

export const deviceHealthApi = {
  summary: (q: { scope: DeviceHealthScope; id?: string; window?: DeviceHealthWindow }) =>
    get<DeviceHealthSummary>(`/summary?${qs({ scope: q.scope, id: q.id, window: q.window || '24h' })}`),
  /** `deviceId` scopes to one till and adds its own `deviceCount` / `deviceLastSeen`. */
  problems: async (q: { tenantId?: string; deviceId?: string; status?: 'open' | 'resolved' | 'all'; window?: DeviceHealthWindow }) =>
    (await get<{ problems: DeviceHealthProblem[] }>(`/problems?${qs({ tenantId: q.tenantId, deviceId: q.deviceId, status: q.status || 'open', window: q.window || '24h' })}`)).problems,
  timeline: async (deviceId: string, window: DeviceHealthWindow = '24h') =>
    (await get<{ events: DeviceHealthTimelineEvent[] }>(`/devices/${encodeURIComponent(deviceId)}/events?${qs({ window })}`)).events,
};
